import React, { useState } from 'react';
import styled from 'styled-components';
import { keyframes } from 'styled-components';
import { 
  Button, 
  FormGroup, 
  FormLabel, 
  FormInput, 
  FormTextarea,
  FormError
} from '../../../styles/components';

// Animation keyframes
const floatAnimation = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0); }
`;

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(99, 102, 241, 0); }
  100% { box-shadow: 0 0 0 0 rgba(99, 102, 241, 0); }
`;

// Styled Components
const ApplicationContainer = styled.div`
  width: 100%;
  max-width: 100%;
  position: relative;
  padding: 20px;
  color: rgba(255, 255, 255, 0.9);
`;

const ApplicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  position: relative;
  
  h2 {
    margin: 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  .close-button {
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    border: none;
    font-size: 1.75rem;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    position: absolute;
    top: 0;
    right: 0;

    &:hover {
      color: rgba(255, 255, 255, 0.9);
    }
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  background: rgba(15, 23, 42, 0.3);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-size: 1.25rem;
  }
`;

const FileInputContainer = styled.div`
  margin-bottom: 1rem;
`;

const FileInput = styled.input`
  width: 100%;
  background: rgba(15, 23, 42, 0.4);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.3);
  outline: none;
  
  &:focus {
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

const FilePreview = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RemoveButton = styled.button`
  background: transparent;
  color: rgba(239, 68, 68, 0.8);
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: rgba(239, 68, 68, 1);
  }
`;

const SubmitButton = styled(Button)`
  background: rgba(79, 70, 229, 0.7);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    background: rgba(79, 70, 229, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: rgba(15, 23, 42, 0.4);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  
  &:hover {
    background: rgba(15, 23, 42, 0.6);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SuccessMessage = styled.div`
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  animation: ${floatAnimation} 4s infinite ease-in-out;
  
  h3 {
    color: rgba(16, 185, 129, 0.9);
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  p {
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const SuccessButton = styled(Button)`
  background: rgba(16, 185, 129, 0.7);
  color: white;
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-weight: 600;
  margin-top: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${pulseAnimation} 2s infinite;
  
  &:hover {
    background: rgba(16, 185, 129, 0.9);
    transform: translateY(-2px);
  }
`;

const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 1rem;
`;

const StyledLabel = styled(FormLabel)`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin-bottom: 0.5rem;
  display: block;
`;

const StyledInput = styled(FormInput)`
  background: rgba(15, 23, 42, 0.4);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border-color: rgba(99, 102, 241, 0.6);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const StyledTextarea = styled(FormTextarea)`
  background: rgba(15, 23, 42, 0.4);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(99, 102, 241, 0.3);
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    background: rgba(15, 23, 42, 0.6);
    border-color: rgba(99, 102, 241, 0.6);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ErrorText = styled(FormError)`
  color: rgba(239, 68, 68, 0.9);
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const InternshipApplication = ({ internship, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    coverLetter: '',
    availability: '',
    expectedSalary: '',
    resume: null,
    additionalDocuments: [],
  });
  
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const { name, files } = e.target;
    if (name === 'resume' && files.length > 0) {
      setFormData(prevData => ({
        ...prevData,
        resume: files[0]
      }));
    } else if (name === 'additionalDocuments' && files.length > 0) {
      setFormData(prevData => ({
        ...prevData,
        additionalDocuments: [...prevData.additionalDocuments, ...Array.from(files)]
      }));
    }
  };

  const removeFile = (type, index) => {
    if (type === 'resume') {
      setFormData(prevData => ({
        ...prevData,
        resume: null
      }));
    } else if (type === 'additionalDocuments') {
      setFormData(prevData => ({
        ...prevData,
        additionalDocuments: prevData.additionalDocuments.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.coverLetter.trim()) {
      newErrors.coverLetter = 'Cover letter is required';
    }
    
    if (!formData.availability.trim()) {
      newErrors.availability = 'Availability information is required';
    }
    
    if (!formData.resume) {
      newErrors.resume = 'Resume is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (validateForm()) {
      // Simulate API call
      setTimeout(() => {
        console.log('Form submitted with data:', formData);
        
        if (onSubmit) {
          onSubmit(formData);
        }
        
        setIsSubmitting(false);
        setSubmitted(true);
      }, 1500);
    } else {
      setIsSubmitting(false);
    }
  };
  
  if (submitted) {
    return (
      <ApplicationContainer>
        <SuccessMessage>
          <h3>Application Submitted Successfully!</h3>
          <p>Thank you for applying to {internship.title} at {internship.company}.</p>
          <p>We will review your application and get back to you soon.</p>
          <SuccessButton onClick={onClose}>Close</SuccessButton>
        </SuccessMessage>
      </ApplicationContainer>
    );
  }

  return (
    <ApplicationContainer>
      <ApplicationHeader>
        <h2>Apply for {internship.title}</h2>
        <button className="close-button" onClick={onClose}>&times;</button>
      </ApplicationHeader>
      
      <FormSection>
        <h3>Company & Internship Details</h3>
        <p><strong>Company:</strong> {internship.company}</p>
        <p><strong>Location:</strong> {internship.location}</p>
        <p><strong>Position:</strong> {internship.title}</p>
        <p><strong>Duration:</strong> {internship.duration || '3 months'}</p>
        <p><strong>Compensation:</strong> {internship.compensation || 'Paid'}</p>
      </FormSection>
      
      <StyledForm onSubmit={handleSubmit}>
        <FormSection>
          <h3>Application Details</h3>
          
          <StyledFormGroup>
            <StyledLabel htmlFor="coverLetter">Cover Letter</StyledLabel>
            <StyledTextarea
              id="coverLetter"
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleInputChange}
              placeholder="Introduce yourself and explain why you're interested in this internship and how your skills align with the requirements..."
              rows="6"
            />
            {errors.coverLetter && <ErrorText>{errors.coverLetter}</ErrorText>}
          </StyledFormGroup>
          
          <StyledFormGroup>
            <StyledLabel htmlFor="availability">Availability</StyledLabel>
            <StyledInput
              type="text"
              id="availability"
              name="availability"
              value={formData.availability}
              onChange={handleInputChange}
              placeholder="When can you start? What is your availability during the internship period?"
            />
            {errors.availability && <ErrorText>{errors.availability}</ErrorText>}
          </StyledFormGroup>
          
          <StyledFormGroup>
            <StyledLabel htmlFor="expectedSalary">Expected Salary/Stipend (optional)</StyledLabel>
            <StyledInput
              type="text"
              id="expectedSalary"
              name="expectedSalary"
              value={formData.expectedSalary}
              onChange={handleInputChange}
              placeholder="What are your salary expectations? (Leave blank if the listed amount is acceptable)"
            />
          </StyledFormGroup>
        </FormSection>
        
        <FormSection>
          <h3>Documents</h3>
          
          <FileInputContainer>
            <StyledLabel htmlFor="resume">Resume/CV *</StyledLabel>
            <FileInput
              type="file"
              id="resume"
              name="resume"
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx"
            />
            {formData.resume && (
              <FilePreview>
                <span>{formData.resume.name}</span>
                <RemoveButton type="button" onClick={() => removeFile('resume')}>Remove</RemoveButton>
              </FilePreview>
            )}
            {errors.resume && <ErrorText>{errors.resume}</ErrorText>}
          </FileInputContainer>
          
          <FileInputContainer>
            <StyledLabel htmlFor="additionalDocuments">Additional Documents (optional)</StyledLabel>
            <FileInput
              type="file"
              id="additionalDocuments"
              name="additionalDocuments"
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              multiple
            />
            {formData.additionalDocuments.length > 0 && (
              <div>
                {formData.additionalDocuments.map((file, index) => (
                  <FilePreview key={index}>
                    <span>{file.name}</span>
                    <RemoveButton type="button" onClick={() => removeFile('additionalDocuments', index)}>
                      Remove
                    </RemoveButton>
                  </FilePreview>
                ))}
              </div>
            )}
          </FileInputContainer>
        </FormSection>
        
        <ButtonGroup>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </SubmitButton>
          <CancelButton type="button" onClick={onClose}>Cancel</CancelButton>
        </ButtonGroup>
      </StyledForm>
    </ApplicationContainer>
  );
};

export default InternshipApplication;
